import { MouseEvent } from 'react';
import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';
import {
  isWorkerSuspended,
  SUSPENDED_BADGE_LABEL,
} from '@/util/dependabilityBadge';
import { tierColor } from './tierColors';
import { InfoTipIcon } from './icons';

// GRAVY-10347: the WHOLE pill opens the tier popover. QA flagged that only the
// ~30px info icon was clickable while the label was an inert <span>, which is a
// small touch target and misses the AC intent ("the tier badge has a clickable
// info affordance"). When `onInfoClick` is set the pill renders as a <button>
// spanning the full label — the button chrome is reset so it looks identical to
// the plain-span (non-interactive) form.
const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  color: '#FFFFFF',
  fontSize: '$sm',
  fontWeight: 700,
  variants: {
    interactive: {
      true: {
        appearance: 'none',
        border: 'none',
        fontFamily: 'inherit',
        fontSize: '$sm',
        fontWeight: 700,
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
        cursor: 'pointer',
      },
    },
  },
});

// Trailing info glyph — now purely decorative (the pill itself is the button),
// so it is hidden from assistive tech. Inherits the pill's text color (white on
// a solid tier/suspended chip, green on the transparent "New worker" badge).
const InfoIcon = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
});

// Medal glyph leading the pill (Figma export 37920:11819) — white, so it
// reads on every tier background.
const MedalIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-testid="tier-pill-medal"
  >
    <path
      d="M15.1266 4.00024C15.1266 8.2402 13.6066 13.2005 8.16666 15.2004C2.66279 13.1525 1.49951 6.88034 1.60611 4.00024L8.16666 0.800049L15.1266 4.00024ZM7.02994 6.22388L4.06021 6.61548L6.23306 8.67798L5.68716 11.6243L8.32095 10.1956L10.9538 11.6243L10.4088 8.67798L12.5817 6.61548L9.61099 6.22388L8.32095 3.51978L7.02994 6.22388Z"
      fill="currentColor"
    />
  </svg>
);

// Sparkles glyph leading the "New worker" badge (heroicons/sparkles, per Figma
// node 40229:56375) — currentColor, so it takes the badge's green text color.
const SparklesIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    data-testid="new-worker-sparkles"
  >
    <path
      d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.898l-2.051-.683a1 1 0 01-.633-.633L6.95 5.684zM13.949 13.684a1 1 0 00-1.898 0l-.184.551a1 1 0 01-.632.633l-.551.183a1 1 0 000 1.898l.551.183a1 1 0 01.633.633l.183.551a1 1 0 001.898 0l.184-.551a1 1 0 01.632-.633l.551-.183a1 1 0 000-1.898l-.551-.184a1 1 0 01-.633-.632l-.183-.551z"
      fill="currentColor"
    />
  </svg>
);

interface Props {
  tier: string;
  label: string;
  onInfoClick?: () => void;
  // open/closed state of the tier popover this button controls — surfaced as
  // aria-expanded so assistive tech gets the state (fix round 3)
  expanded?: boolean;
  // GRAVY-10404: DISPLAY-ONLY. A suspended / permanently-removed worker shows a
  // dark "Suspended" pill in place of the tier — distinct from Monitored (red)
  // and the no-show caution badge. The score number is untouched.
  accountabilityStatus?: string | null;
  // GRAVY-10404: authoritative active-suspension flag from the backend (reflects
  // an active suspension record — temporary OR permanent — the same signal the
  // suspension banner uses). When true the pill shows "Suspended" regardless of
  // accountabilityStatus, so the badge agrees with the banner even if the
  // denormalized status column lags. Display-only.
  isSuspended?: boolean | null;
  // Activation-mode workers (fewer than 5 approved shifts) have not earned a
  // shift-based tier yet, so the badge slot shows a "New worker" badge instead of
  // a tier (Figma node 40007-12129). Reliability workers show their tier.
  scoringMode?: 'activation' | 'reliability';
}

const NEW_WORKER_LABEL = 'New worker';

const TierPill = ({
  tier,
  label,
  onInfoClick,
  expanded = false,
  accountabilityStatus,
  isSuspended,
  scoringMode,
}: Props) => {
  const suspended = isWorkerSuspended(accountabilityStatus, isSuspended);
  // Suspension outranks the new-worker badge: a suspended activation worker still
  // shows the dark "Suspended" chip, matching the suspension banner.
  const isNewWorker = !suspended && scoringMode === 'activation';
  const interactive = Boolean(onInfoClick);
  const pillLabel = suspended
    ? SUSPENDED_BADGE_LABEL
    : isNewWorker
      ? NEW_WORKER_LABEL
      : label;

  const content = (
    <>
      {/* The medal implies a tier ranking. A suspended worker no longer holds one
          (dropped, plain dark chip); a new worker has not earned one yet (sparkles
          badge instead). */}
      {suspended ? null : isNewWorker ? <SparklesIcon /> : <MedalIcon />}
      {pillLabel}
      {interactive && (
        <InfoIcon aria-hidden="true">
          <InfoTipIcon />
        </InfoIcon>
      )}
    </>
  );

  // New worker = green text on no fill (per Figma); tier / suspended = white text
  // on a solid color.
  const background = suspended
    ? dependabilityV2Colors.depSuspended
    : isNewWorker
      ? 'transparent'
      : tierColor(tier);
  const textColor = isNewWorker
    ? dependabilityV2Colors.depNewWorkerText
    : '#FFFFFF';

  if (!interactive) {
    return (
      <Pill
        css={{ backgroundColor: background, color: textColor }}
        data-testid="tier-pill"
        data-suspended={suspended || undefined}
        data-mode={isNewWorker ? 'activation' : undefined}
      >
        {content}
      </Pill>
    );
  }

  return (
    <Pill
      as="button"
      type="button"
      interactive
      css={{ backgroundColor: background, color: textColor }}
      data-testid="tier-pill"
      data-suspended={suspended || undefined}
      data-mode={isNewWorker ? 'activation' : undefined}
      aria-label="Dependability tier info"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      // GRAVY-10233: stop the opening click here. TierPopover's shared Popover
      // arms a window-level `useClickout` the moment it opens; without this, the
      // very click that opened the popover keeps bubbling to window, the clickout
      // listener fires, and the popover closes ~1ms after it opened — so a real
      // user click flashed it open then shut ("nothing happens"). (Reproduced on
      // live QA; jest/dispatchEvent miss the race itself as they don't bubble the
      // real click to window under the same effect timing.)
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        // The "tier info opened" analytics is fired by the card's openPopover
        // (which onInfoClick calls) so it also captures hover/focus opens, not
        // just clicks. Keep the stopPropagation clickout guard here.
        onInfoClick?.();
      }}
    >
      {content}
    </Pill>
  );
};

export default TierPill;
